// ResponsiveGridLayout.jsx
import React from 'react';
import { Grid } from '@mui/material';
import { NewCard } from './NewCard';

/**
 * A reusable responsive grid layout component
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render inside the grid
 * @param {boolean} props.showNewCard - Whether to show the 'new' card
 * @param {Object} props.newCardProps - Props for the NewCard component
 * @param {Object} props.gridProps - Additional props for the grid container
 * @param {Object} props.containerProps - Additional props for the outer container
 */

// Define interfaces for the props
interface NewCardProps {
    title: string;
    description: string;
    type: any; // Replace 'any' with your actual CardTypes type
    onClick: () => void;
}
  
interface ResponsiveGridLayoutProps {
    children: React.ReactNode;
    showNewCard?: boolean;
    newCardProps?: NewCardProps;
    gridProps?: React.CSSProperties;
    containerProps?: React.CSSProperties;
}

export const ResponsiveGridLayout: React.FC<ResponsiveGridLayoutProps> = ({ 
  children, 
  showNewCard = false, 
  newCardProps, 
  gridProps = {}, 
  containerProps = {} 
}) => {
  return (
    <Grid sx={{
      width: '100%',
      mx: 'auto',
      px: { xs: 2, sm: 3 },
      ...containerProps
    }}>
      <Grid 
        container 
        sx={{ 
          display: 'grid',
          // Based on card width (385px) + spacing (24px) = ~409px per card
          gridTemplateColumns: {
            xs: 'repeat(1, 385px)',
            '@media (min-width: 450px) and (max-width: 859px)': {
              gridTemplateColumns: 'repeat(1, 385px)'
            },
            '@media (min-width: 860px) and (max-width: 1269px)': {
              gridTemplateColumns: 'repeat(2, 385px)'
            },
            '@media (min-width: 1270px) and (max-width: 1679px)': {
              gridTemplateColumns: 'repeat(3, 385px)'
            },
            '@media (min-width: 1680px)': {
              gridTemplateColumns: 'repeat(4, 385px)'
            }
          },
          gap: 3,
          justifyContent: 'center',
          '& .MuiGrid-item': {
            padding: 0,
            width: '385px'
          },
          ...gridProps
        }}
      >
        {showNewCard && newCardProps && (
          <Grid item key={'new'} sx={{ width: '385px' }}>
            <NewCard 
              title={newCardProps.title}
              description={newCardProps.description}
              type={newCardProps.type}
              onClick={newCardProps.onClick}
            />
          </Grid>
        )}
        {children}
      </Grid>
    </Grid>
  );
};